/**
 * Política de erro HTTP do client autenticado do Corp (401 refresh, 403 termos).
 * Mantém api/client.ts enxuto; regras de negócio ficam aqui.
 */

import type { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '@/stores/auth'
import {
  getPrimaryApiErrorCode,
  isCorpManagerTermsFlowCode,
} from '@/types/apiErrors'

/** Evita loop: no máximo um retry por request em 401. */
export const RETRIED_KEY = '__retried' as const

/** GET /manager/me: não redirecionar em 403 terms_not_accepted (caller trata). */
export const SKIP_TERMS_REDIRECT_KEY = 'skipTermsRedirect' as const

export type RequestConfigWithFlags = InternalAxiosRequestConfig & {
  [RETRIED_KEY]?: boolean
  [SKIP_TERMS_REDIRECT_KEY]?: boolean
}

function redirectToAcceptTerms(): void {
  const base = (import.meta.env.BASE_URL || '/').replace(/\/?$/, '/')
  window.location.href = `${window.location.origin}${base}auth/accept-terms`
}

function throwAfterTermsRedirect(code: string): never {
  const msg =
    code === 'terms_not_accepted'
      ? 'Terms not accepted'
      : 'Authorization error - complete registration'
  throw new Error(msg)
}

export type CorpAuthenticatedErrorPolicyOutcome =
  | { kind: 'retry'; promise: Promise<AxiosResponse> }
  | { kind: 'fallthrough' }

async function tryUnauthorizedRetry(
  instance: AxiosInstance,
  error: AxiosError,
  config: RequestConfigWithFlags | undefined
): Promise<AxiosResponse | null> {
  if (error.response?.status !== 401 || !config || config[RETRIED_KEY]) return null
  const store = useAuthStore()
  const refreshed = await store.forceRefresh()
  if (refreshed) {
    config[RETRIED_KEY] = true
    return instance.request(config)
  }
  store.redirectToLogin()
  return null
}

function tryForbiddenTermsRedirect(
  error: AxiosError,
  config: RequestConfigWithFlags | undefined
): void {
  if (error.response?.status !== 403) return
  const skip = config?.[SKIP_TERMS_REDIRECT_KEY] === true
  const code = getPrimaryApiErrorCode(error.response?.data)
  if (!skip && code !== undefined && isCorpManagerTermsFlowCode(code)) {
    redirectToAcceptTerms()
    throwAfterTermsRedirect(code)
  }
}

/**
 * 401: refresh + retry uma vez; se falhar, redirect login (fluxo segue para handleError).
 * 403: redirect aceitar termos quando aplicável; senão fallthrough.
 */
export async function runCorpAuthenticatedErrorPolicy(
  instance: AxiosInstance,
  error: AxiosError
): Promise<CorpAuthenticatedErrorPolicyOutcome> {
  const config = error.config as RequestConfigWithFlags | undefined
  const retriedResponse = await tryUnauthorizedRetry(instance, error, config)
  if (retriedResponse !== null) {
    return { kind: 'retry', promise: Promise.resolve(retriedResponse) }
  }
  tryForbiddenTermsRedirect(error, config)
  return { kind: 'fallthrough' }
}
